import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  /** Скрыть стандартный заголовок (для кастомных layout — напр. success-иконка). */
  hideHeader?: boolean;
}

/**
 * Модалка по макетам РТК. Десктоп — белая карточка по центру и отдельная кнопка ×
 * за её правой границей, выровненная по верхнему краю. Мобильная — bottom-sheet во
 * всю ширину со скруглением сверху и × внутри карточки. Focus-trap/Esc — из Radix.
 *
 * Кнопка × намеренно вынесена из скроллящейся карточки: если элемент со
 * `overflow-y: auto` содержит абсолютно спозиционированного потомка за своей
 * границей, браузер вычисляет `overflow-x` в `auto` и рисует горизонтальный
 * скроллбар (замечание РТК promt-82 §4). Здесь `Dialog.Content` только
 * позиционирует, а скроллит вложенная карточка.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  hideHeader,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className={cn(
            'fixed z-50 focus:outline-none',
            'inset-x-0 bottom-0',
            'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-5rem)] sm:max-w-md',
            'sm:-translate-x-1/2 sm:-translate-y-1/2',
            className
          )}
        >
          {/* Мобильный — внутри контура, серый почти квадратный фон. Десктоп — целиком за
              правой границей карточки и вровень с её верхом, чтобы кнопка не наезжала на
              угол (замечание 4, promt-86). Места хватает: карточка ограничена max-w-md. */}
          <Dialog.Close
            className={cn(
              'absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-xl bg-page text-fg-muted transition-colors hover:text-fg',
              'sm:left-full sm:right-auto sm:top-0 sm:ml-3 sm:h-11 sm:w-11 sm:rounded-2xl sm:bg-surface sm:shadow-pop sm:hover:bg-page'
            )}
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </Dialog.Close>

          <div
            className={cn(
              'max-h-[92vh] overflow-y-auto overflow-x-hidden bg-surface shadow-pop',
              'rounded-t-3xl p-6 pb-8',
              'sm:max-h-[85vh] sm:rounded-2xl sm:pb-6'
            )}
          >
            {!hideHeader && (title != null || description != null) && (
              <div className="mb-4 space-y-1.5 pr-10 text-center sm:pr-0 sm:text-left">
                {title && <Dialog.Title className="text-xl font-bold text-fg">{title}</Dialog.Title>}
                {description && (
                  <Dialog.Description className="text-sm text-fg-muted">{description}</Dialog.Description>
                )}
              </div>
            )}

            {children}

            {footer && <ModalActions>{footer}</ModalActions>}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Блок кнопок модалки: на мобильной — вертикальный стек во всю ширину
 * (главное действие сверху), на десктопе — в строку по содержимому.
 */
export function ModalActions({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mt-6 flex flex-col gap-3 [&>*]:w-full',
        'sm:flex-row sm:flex-wrap sm:[&>*]:w-auto',
        className
      )}
    >
      {children}
    </div>
  );
}
